import { isEqualWith } from 'lodash';
import {
  ExteriorNodeLabelModel,
  ExteriorNodeLabelModelPosition,
  FreeEdgeLabelModel,
  IEdge,
  IGraph,
  ILabel,
  ILabelOwner,
  IModelItem,
  INode,
  INodeStyle,
  Insets,
  Rect,
  Size,
} from '@ardoq/yfiles';
import {
  GraphItem,
  MetaModelViewGraphNode,
  type GraphEdgeBase,
} from '@ardoq/graph';
import * as encodingUtils from '@ardoq/html';
import { logError } from '@ardoq/logging';
import { DiffType } from '@ardoq/data-model';
import { ArdoqLabelStyle } from './ArdoqLabelStyleClone';
import { getDefaultSize } from 'yfilesExtensions/styles/styleLoader';
import { getNodeLayoutSize } from 'yfilesExtensions/getNodeLayoutSize';
import {
  CONTEXT_HIGHLIGHT_PADDING,
  CONTEXT_HIGHLIGHT_STROKE,
  NODE_HEIGHT_WIDTH_SELECTION as NODE_HEIGHT,
  NODE_WIDTH,
} from 'yfilesExtensions/styles/consts';
import { EdgeStyle } from './EdgeStyleClone';
import { isContextNode } from '../../utils';
import { diffUpdateGraph } from 'yfilesExtensions/utils/diffUpdateGraph';

type GraphViewData = {
  nodes: MetaModelViewGraphNode[];
  edges: GraphEdgeBase[];
};

const graphItemsEqual = (a: GraphItem | GraphItem[], b: GraphItem) =>
  !Array.isArray(a) && a.isEqual(b);

const updateLabels = (
  owner: ILabelOwner,
  graph: IGraph,
  graphItem: GraphItem
) => {
  const labelsToRemove: ILabel[] = [];
  const isNode = owner instanceof INode;
  const isContext = isNode && isContextNode(owner);
  owner.labels.forEach(label => {
    if (label.tag === null) {
      graph.setLabelText(
        label,
        encodingUtils.unescapeHTML(graphItem.getLabelWithCount())
      );
    }
    if (isNode) {
      graph.setLabelLayoutParameter(
        label,
        isContext ? contextNodeLabelParameter : southsideLabelParameter
      );
    }
  });
  labelsToRemove.forEach(label => graph.remove(label));
};

const labelStyle = ArdoqLabelStyle.Instance;
const southsideLabelParameter = new ExteriorNodeLabelModel({
  margins: CONTEXT_HIGHLIGHT_STROKE,
}).createParameter(ExteriorNodeLabelModelPosition.BOTTOM);
const contextNodeLabelParameter = new ExteriorNodeLabelModel({
  margins: new Insets(
    0,
    0,
    CONTEXT_HIGHLIGHT_STROKE + CONTEXT_HIGHLIGHT_PADDING,
    0
  ),
}).createParameter(ExteriorNodeLabelModelPosition.BOTTOM);

export class CollapsibleGroupsYGraphBuilderClone {
  private edgeMap = new Map<string, IEdge>();
  private nodeMap = new Map<string, INode>();
  nodesSource: MetaModelViewGraphNode[] = [];
  edgesSource: GraphEdgeBase[] = [];
  constructor(
    private graph: IGraph,
    private getNodeStyle: (node: MetaModelViewGraphNode) => INodeStyle,
    private getNodeSize: (nodeStyle: INodeStyle) => Size
  ) {
    // Initialization
    graph.edgeDefaults.style = new EdgeStyle();
    // Style and labels
    graph.nodeDefaults.labels.layoutParameter = southsideLabelParameter;
    graph.edgeDefaults.labels.style = labelStyle;
    graph.nodeDefaults.labels.style = labelStyle;

    const edgeLabelModel = new FreeEdgeLabelModel();
    graph.edgeDefaults.labels.layoutParameter =
      edgeLabelModel.createParameter();
  }
  private createEdge = (graphEdge: GraphEdgeBase) => {
    const sourceNode = this.nodeMap.get(graphEdge.source);
    const targetNode = this.nodeMap.get(graphEdge.target);
    if (!sourceNode || !targetNode) {
      logError(Error('Source or Target node not found while creating Edge.'));
      return null;
    }
    const edge = this.graph.createEdge({
      tag: graphEdge,
      labels: [encodingUtils.unescapeHTML(graphEdge.getLabel())],
      source: sourceNode,
      target: targetNode,
    });
    updateLabels(edge, this.graph, graphEdge);

    return edge;
  };
  private updateEdge = (graphEdge: GraphEdgeBase) => {
    const edge = this.edgeMap.get(graphEdge.id);
    if (!edge) {
      logError(Error('Edge not found in updateEdge.'));
      return null;
    }

    // Create new edge if sourcePort/targetPort is gone (group Node turned into a normal node or vice-versa)
    if (!this.graph.contains(edge) || !edge.sourcePort || !edge.targetPort) {
      return this.createEdge(graphEdge);
    }

    if (
      edge.sourceNode?.tag instanceof GraphItem &&
      edge.targetNode?.tag instanceof GraphItem
    ) {
      if (
        edge.sourceNode.tag.id !== graphEdge.source ||
        edge.targetNode.tag.id !== graphEdge.target
      ) {
        // if source or target has changed, we must create a new edge.
        this.removeEdge(edge);
        return this.createEdge(graphEdge);
      }
    } else {
      logError(Error('Unexpected edge source or target.'));
    }
    updateLabels(edge, this.graph, graphEdge);
    return edge;
  };

  private createNode = (graphNode: MetaModelViewGraphNode) => {
    const node = this.graph.createNode({
      parent: null,
      tag: graphNode,
      labels: [encodingUtils.unescapeHTML(graphNode.getLabelWithCount())],
    });
    this.updateNodeLayout(node);
    updateLabels(node, this.graph, graphNode);
    return node;
  };
  private updateNode = (graphNode: MetaModelViewGraphNode) => {
    const node = this.nodeMap.get(graphNode.id)!;
    if (!node) {
      logError(Error('Node not found in updateNode.'));
      return null;
    }
    const style = this.getNodeStyle(graphNode);

    if (style !== node.style) {
      this.graph.setStyle(node, style);
    }
    this.updateNodeLayout(node);

    updateLabels(node, this.graph, graphNode);

    this.graph.setParent(node, null);

    return node;
  };

  private removeItem = (item: IModelItem, map: Map<string, IModelItem>) => {
    const businessObject: GraphItem = item.tag;
    map.delete(businessObject.id);
    if (this.graph.contains(item)) {
      this.graph.remove(item);
    }
  };
  private removeEdge = (edge: IEdge) => {
    // Make sure the edge is attached to the graph
    if (this.graph.contains(edge) && (edge.sourcePort || edge.targetPort)) {
      this.removeItem(edge, this.edgeMap);
    }
  };
  hasLayoutUpdate({ nodes, edges }: GraphViewData) {
    const nodesUnchanged =
      !nodes || isEqualWith(nodes, this.nodesSource, graphItemsEqual);
    const groupsUnchanged = true;
    const edgesUnchanged =
      !edges || isEqualWith(edges, this.edgesSource, graphItemsEqual);

    return !(nodesUnchanged && groupsUnchanged && edgesUnchanged);
  }
  clearEdges() {
    Array.from(this.edgeMap.values()).forEach(this.removeEdge);
  }
  clear() {
    this.graph.clear();
    this.nodeMap.clear();
    this.edgeMap.clear();
  }
  buildGraph() {
    this.clear();

    this.nodeMap = new Map(
      this.nodesSource.map(graphNode => [
        graphNode.id,
        this.createNode(graphNode),
      ])
    );

    this.edgeMap = new Map(
      this.edgesSource.map(graphEdge => {
        return [graphEdge.id, this.createEdge(graphEdge)!];
      })
    );
  }

  updateGraph() {
    if (this.nodeMap.size + this.edgeMap.size === 0) {
      this.buildGraph();
      return;
    }

    this.nodeMap = diffUpdateGraph<MetaModelViewGraphNode, INode>({
      oldMap: this.nodeMap,
      businessObjects: this.nodesSource,
      create: this.createNode,
      update: this.updateNode,
      remove: this.removeItem,
    });

    this.edgeMap = diffUpdateGraph({
      oldMap: this.edgeMap,
      businessObjects: this.edgesSource,
      create: this.createEdge,
      update: this.updateEdge,
      remove: this.removeEdge,
    });
  }

  private updateNodeLayout(node: INode) {
    const style = this.getNodeStyle(node.tag);
    const size = this.getNodeSize(style);
    const styleWidth = size?.width || getDefaultSize().width;
    const styleHeight = size?.height || getDefaultSize().height;
    const yAspectRatio = NODE_HEIGHT / styleHeight;
    const xAspectRatio = NODE_WIDTH / styleWidth;

    let width;
    let height;

    if (styleWidth * yAspectRatio > NODE_WIDTH) {
      width = NODE_WIDTH;
      height = styleHeight * xAspectRatio;
    } else {
      width = styleWidth * yAspectRatio;
      height = NODE_HEIGHT;
    }

    this.graph.setStyle(node, style);
    this.graph.setNodeLayout(
      node,
      new Rect(
        node.layout.topLeft,
        getNodeLayoutSize({
          styleWidth: width,
          styleHeight: height,
          hasContextHighlight: false,
          contextHighlightPadding: 0,
          visualDiffType:
            node.tag instanceof GraphItem
              ? node.tag.getVisualDiffType()
              : DiffType.NONE,
        })
      )
    );
  }
}
